import { useEffect } from "react";

import {
  Column,
  ErrorText,
  LoadingIndicator,
  Row,
  TopbarContainer,
  TopbarTitle,
} from "@/components";
import { SadieSimulatorControls } from "@/components/tools/sadie-simulator/SadieSimulatorControls";
import { SadieSimulatorConversation } from "@/components/tools/sadie-simulator/SadieSimulatorConversation";
import { Button } from "@ui/button";

import { useSadieSimulator } from "@/hooks/useSadieSimulator";
import { useSetTitle } from "@/hooks/useSetTitle";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const AGENT_NAME = "Sadie";

export const ToolsSadieSimulatorPage = () => {
  /* React Hooks */
  const simulator = useSadieSimulator();

  useSetTitle({ title: "Tools | Sadie Simulator" });

  useEffect(() => {
    mixpanelUtils.trackScreenView(MIXPANEL_SCREENS.SADIE_SIMULATOR);
  }, []);

  /* Render */
  const render = () => {
    const { defaults, preset, selectedPersona, status } = simulator;
    if (status === "pending") return <LoadingIndicator variant="medium" />;
    if (status === "error" || !defaults || !preset || !selectedPersona) {
      return (
        <Row className="items-center justify-center gap-2 py-2">
          <ErrorText errorMessage="Could not load the simulator defaults." />
          <Button
            onClick={() => void simulator.refetchDefaults()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        </Row>
      );
    }
    return (
      // Each pane scrolls on its own. Without the overflow here, a controls
      // column taller than the window scrolls the whole page, and the
      // conversation's scroll-to-latest drags the page with it.
      <Row className="min-h-0 flex-1 overflow-hidden">
        <Column className="min-h-0 w-[400px] shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50 p-4">
          <SadieSimulatorControls
            chunked={simulator.chunked}
            defaultOpenerInstructions={
              simulator.defaultOpenerInstructions ?? defaults.openerInstructions
            }
            disabled={simulator.isTurnPending || simulator.isLoadingDefaults}
            instructions={simulator.activeInstructions}
            instructionsDirty={simulator.isInstructionsDirty}
            isFitError={simulator.isFitError}
            isFitPending={simulator.isFitPending}
            isLiveFit={simulator.isLiveFit}
            isOpenerDirty={simulator.isOpenerDirty}
            liveFits={simulator.liveFits}
            matchedOpportunities={defaults.matchedOpportunities}
            model={simulator.activeModel}
            models={defaults.models}
            onChunkedChange={simulator.handleChunkedChange}
            onInstructionsChange={simulator.setInstructions}
            onMatchedOpportunityChange={
              simulator.handleMatchedOpportunityChange
            }
            onModelChange={simulator.setModel}
            onOpenerInstructionsChange={
              simulator.handleOpenerInstructionsChange
            }
            onPersonaChange={simulator.handlePersonaChange}
            onPresetChange={simulator.handlePresetChange}
            onResetInstructions={simulator.handleResetInstructions}
            onResetOpenerInstructions={simulator.handleResetOpenerInstructions}
            onRetryFit={simulator.handleRetryFit}
            onSessionKindChange={simulator.handleSessionKindChange}
            openerInstructions={
              simulator.openerInstructions ?? defaults.openerInstructions
            }
            personas={defaults.personas}
            presets={defaults.presets}
            selectedMatchedOpportunity={
              simulator.selectedMatchedOpportunity ??
              defaults.selectedMatchedOpportunity
            }
            selectedPersona={selectedPersona}
            selectedPreset={preset.key}
            sessionKind={simulator.sessionKind}
            jobSearchSetup={simulator.jobSearchSetup}
            postPipelineSetup={simulator.postPipelineSetup}
            workerInitiatedSetup={simulator.workerInitiatedSetup}
          />
        </Column>
        <Column className="min-h-0 flex-1">
          {simulator.isTurnError ? (
            <Row className="items-center justify-center gap-2 py-2">
              <ErrorText errorMessage="That turn failed." />
              <Button
                onClick={simulator.handleRetry}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            </Row>
          ) : null}
          <SadieSimulatorConversation
            agentName={AGENT_NAME}
            entries={simulator.entries}
            isEnded={simulator.isEnded}
            isFollowUpAvailable={simulator.isFollowUpAvailable}
            isHandoffAvailable={simulator.isHandoffAvailable}
            isHandoffPending={simulator.isHandoffPending}
            isLoopedToSession1={simulator.isLoopedToSession1}
            isPending={simulator.isTurnPending || simulator.isSearchPending}
            isSetupLoading={simulator.isLoadingDefaults}
            onContinueToPulse={simulator.handleContinueToPulse}
            onFollowUp={simulator.handleFollowUp}
            onReset={simulator.handleReset}
            onSend={simulator.handleSend}
            onStart={simulator.handleStart}
            priorEntries={simulator.priorEntries}
            workerTextsFirst={simulator.isWorkerInitiated}
          />
        </Column>
      </Row>
    );
  };

  return (
    <Column className="h-svh">
      <TopbarContainer>
        <TopbarTitle>Sadie Simulator</TopbarTitle>
      </TopbarContainer>
      {render()}
    </Column>
  );
};
